import React from "react"
import {
  ExclamationCircleIcon,
  ExclamationTriangleIcon,
} from "@heroicons/react/24/solid"
import useDate from "~/ui-rtk/hooks/date"
import type { ConnectorStatus } from "~/ui-rtk/shared/types/connectors"

/**
 * Runtime shape of the `GET /company-connector/:id/airbyte-setup-error`
 * response. The OpenAPI-generated client types this as `{ message: string }`,
 * but the backend also writes `timestamp` and (sometimes) `isValidationError`
 * to Redis. See apps/api/src/services/company-connector/company-connector.service.ts.
 */
export type AirbyteSetupErrorData = {
  message: string
  timestamp?: number
  isValidationError?: boolean
}

/**
 * Computed connector statuses where the backend may have persisted a setup
 * error in Redis. PENDING_SETUP (Airbyte mid-setup) and RECONNECT (backend
 * status PENDING_RECONNECT mapped via getUnifiedConnectorStatus /
 * mapConnectorStatusWithVersionCheck) are the surfaces that produce an error.
 *
 * Use this against the computed `ConnectorStatus` string. For raw API
 * `ConnectionStatus` enum comparisons (e.g. when reading directly from
 * CompanyConnectorDto), compare to `ConnectionStatus.PENDING_SETUP` /
 * `ConnectionStatus.PENDING_RECONNECT` instead.
 */
export const ERROR_ELIGIBLE_STATUSES: readonly ConnectorStatus[] = [
  "PENDING_SETUP",
  "RECONNECT",
]

type SetupErrorBannerProps = {
  error: AirbyteSetupErrorData
  /**
   * When `dense`, the banner uses a compact layout suitable for the connector
   * card. Otherwise it uses the detail-dialog layout.
   */
  variant?: "dense" | "detail"
  className?: string
}

const SetupErrorBanner: React.FC<SetupErrorBannerProps> = ({
  error,
  variant = "detail",
  className = "",
}) => {
  const dayjs = useDate()
  const { message, timestamp, isValidationError } = error

  const heading = isValidationError
    ? "Please check your configuration:"
    : "Something went wrong during setup:"

  const Icon = isValidationError
    ? ExclamationTriangleIcon
    : ExclamationCircleIcon

  // Match the existing "system" warning/error colors used elsewhere in
  // ConnectorsTable. Validation errors use the warning palette, system errors
  // use the error palette.
  const accentText = isValidationError
    ? "text-system-warning-400"
    : "text-system-error-400"
  const borderAccent = isValidationError
    ? "border-system-warning-500/60"
    : "border-system-error-500/60"
  const backgroundAccent = isValidationError
    ? "bg-system-warning-500/10"
    : "bg-system-error-500/10"

  const relativeTimestamp =
    typeof timestamp === "number" && Number.isFinite(timestamp)
      ? dayjs(timestamp).fromNow()
      : null

  if (variant === "dense") {
    return (
      <div
        role="alert"
        data-testid="setup-error-banner"
        className={`flex items-start gap-2 p-2 rounded-md border ${borderAccent} ${backgroundAccent} ${className}`}
      >
        <Icon className={`w-4 h-4 mt-0.5 shrink-0 ${accentText}`} />
        <div className="flex flex-col text-left">
          <p className={`text-xs font-bold ${accentText}`}>{heading}</p>
          <p className="text-xs text-gray-200 leading-snug break-words">
            {message}
          </p>
          {relativeTimestamp && (
            <p className="text-[10px] text-gray-400 mt-0.5">
              Reported {relativeTimestamp}
            </p>
          )}
        </div>
      </div>
    )
  }

  return (
    <div
      role="alert"
      data-testid="setup-error-banner"
      className={`flex flex-col gap-2 text-left mb-4 p-3 rounded-lg border ${borderAccent} ${backgroundAccent} ${className}`}
    >
      <div className="flex items-center gap-2">
        <Icon className={`w-5 h-5 ${accentText}`} />
        <p className="text-4 leading-7 font-bold text-platinum">{heading}</p>
      </div>
      <p className={`text-4 leading-7 font-medium ${accentText} ml-7`}>
        {message}
      </p>
      {relativeTimestamp && (
        <p className="text-3.5 leading-6 text-gray-400 ml-7">
          Reported {relativeTimestamp}
        </p>
      )}
    </div>
  )
}

export default SetupErrorBanner
